import { dinero, traducirError, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, Buscador, Chip, Filtro, IconoMas, Listado, Menu, useBusquedaDiferida, type Columna, type ElementoMenu } from '@tallerya/ui';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { useMoneda } from '../../datos/useMoneda';
import {
  exportar,
  useCambiosItem,
  useCatalogo,
  useCategorias,
  version,
  type FiltrosCatalogo,
  type FormatoExportacion,
  type Item,
} from './datos';
import { horas } from './duracion';
import { ConfirmacionItem, type AccionItem } from './ConfirmacionItem';
import { ImportarItems } from './ImportarItems';

// El listado de Productos/Servicios (#112): «Productos/Servicios - Listado» del
// Figma y sus estados. Sigue a `ListadoClientes.tsx`: filtros en la URL para que un
// enlace o «Atrás» los conserven, el cursor con su pila de «Anterior», y el menú
// de la fila con solo lo que se puede hacer.
//
// Lo propio del catálogo:
//
//   - el filtro de categoría se llena con las categorías activas del taller;
//   - el precio va con la moneda del taller (`useMoneda`), no la del idioma;
//   - la duración se muestra en horas, y la API la manda en minutos;
//   - el «…» abre la ventana de importar (#114) y exporta directo a Excel, CSV
//     o PDF con los filtros puestos: se exporta lo que se ve;
//   - Nuevo, Editar y Duplicar llevan a las rutas del formulario, que llega con
//     #113. Hasta entonces abren «no encontrado»: fue una decisión de #112.

const RUTA = '/productos-servicios';

type FiltrosUrl = Omit<FiltrosCatalogo, 'cursor'>;

/** Un id de categoría bien formado. Lo demás no llega a la API, que respondería 400. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Los filtros que trae la URL. Un valor que no se reconoce —un enlace viejo o
 * editado a mano— cuenta como «Todos», no como error.
 */
function leerFiltros(params: URLSearchParams): FiltrosUrl {
  const tipo = params.get('tipo');
  const estado = params.get('estado');
  return {
    buscar: params.get('buscar') ?? '',
    tipo: tipo === 'service' || tipo === 'product' ? tipo : 'todos',
    categoria: UUID.test(params.get('categoria') ?? '') ? (params.get('categoria') as string) : 'todas',
    estado: estado === 'activos' || estado === 'inactivos' ? estado : 'todos',
  };
}

export function ListadoItems() {
  const { t, idioma } = useTraduccion();
  const navegar = useNavigate();
  const cambios = useCambiosItem();
  const moneda = useMoneda();
  const categorias = useCategorias();
  useTituloPantalla(t('admin.catalogo.titulo'));

  const [params, setParams] = useSearchParams();
  const url = leerFiltros(params);
  const busqueda = params.toString();

  const [texto, setTexto] = useState(url.buscar);
  const [cursor, setCursor] = useState<string | null>(null);
  // Por dónde se pasó: la API solo sabe ir hacia adelante, así que volver es
  // sacar el último cursor de esta pila.
  const [anteriores, setAnteriores] = useState<(string | null)[]>([]);
  const { hacer, problema, setProblema } = useAccionDeFila();
  const [importando, setImportando] = useState(false);
  const [confirmacion, setConfirmacion] = useState<{ accion: AccionItem; id: string } | null>(null);

  // Cuando la URL cambia —un filtro, «Atrás», o tocar el menú lateral— se vuelve
  // a la primera página: el cursor de la búsqueda anterior no significa nada en
  // la nueva. Se ajusta al dibujar, como en `ListadoClientes.tsx`.
  const [vista, setVista] = useState({ busqueda, buscar: url.buscar });
  if (busqueda !== vista.busqueda) {
    setVista({ busqueda, buscar: url.buscar });
    setCursor(null);
    setAnteriores([]);
    if (url.buscar !== vista.buscar) setTexto(url.buscar);
  }

  /**
   * Pone filtros en la URL. Reemplaza la entrada del historial del navegador en vez
   * de sumar una: «Atrás» sale del listado, no deshace un filtro por cada tecla.
   */
  function filtrar(cambio: Partial<FiltrosUrl>) {
    setParams(
      (actuales) => {
        const nuevos = new URLSearchParams(actuales);
        for (const [clave, valor] of Object.entries(cambio)) {
          // «Todos» y «Todas» son los valores de los combos; en el buscador son un texto como cualquiera.
          if (valor && (clave === 'buscar' || (valor !== 'todos' && valor !== 'todas'))) nuevos.set(clave, valor);
          else nuevos.delete(clave);
        }
        return nuevos;
      },
      { replace: true },
    );
  }

  useBusquedaDiferida(texto, (buscar) => filtrar({ buscar }), { pendiente: texto !== url.buscar });

  // Un id bien formado pero que no está entre las categorías activas —una que se
  // desactivó después de guardar el enlace— también cuenta como «Todas»: si no,
  // la lista saldría filtrada con el filtro diciendo «Todas».
  const categoria =
    url.categoria === 'todas' || !categorias.data || categorias.data.some((c) => String(c.id) === url.categoria)
      ? url.categoria
      : 'todas';
  const filtros: FiltrosCatalogo = { ...url, categoria, cursor };
  const consulta = useCatalogo(filtros);
  const pagina = consulta.data;

  /** Baja lo que muestra el listado. Con el estado en «Todos», también los inactivos: el listado los muestra. */
  async function bajar(formato: FormatoExportacion) {
    setProblema(null);
    try {
      const fallo = await exportar(formato, { ...url, categoria: filtros.categoria }, true);
      if (fallo) setProblema(fallo.code ? traducirError(fallo.code, t) : t('admin.catalogo.error.reintentar'));
    } catch {
      setProblema(t('admin.catalogo.error.reintentar'));
    }
  }

  // `desdeListado`: el formulario vuelve con «Atrás», para conservar los filtros.
  const editar = (i: Item) =>
    void navegar(`${RUTA}/${String(i.id)}/editar`, { state: { desdeListado: true } });
  const verFicha = (i: Item) => void navegar(`${RUTA}/${String(i.id)}`, { state: { desdeListado: true } });
  const duplicar = (i: Item) =>
    void navegar(`${RUTA}/nuevo?duplicar=${String(i.id)}`, { state: { desdeListado: true } });

  function opciones(i: Item): ElementoMenu[] {
    const id = String(i.id);
    const nombre = { nombre: i.name };

    return [
      { etiqueta: t('admin.catalogo.menu.verFicha'), onSeleccionar: () => verFicha(i) },
      { etiqueta: t('admin.catalogo.menu.editar'), onSeleccionar: () => editar(i) },
      { etiqueta: t('admin.catalogo.menu.duplicar'), onSeleccionar: () => duplicar(i) },
      { tipo: 'divisor' },
      // Desactivar y Eliminar piden confirmación en su ventana; activar se deshace
      // solo y va directo.
      i.isActive
        ? { etiqueta: t('admin.catalogo.menu.desactivar'), onSeleccionar: () => setConfirmacion({ accion: 'desactivar', id }) }
        : {
            etiqueta: t('admin.catalogo.menu.activar'),
            onSeleccionar: () =>
              void hacer(
                cambios.cambiarEstado(id, version(i.version), true),
                t('admin.catalogo.aviso.activado', nombre),
              ),
          },
      { etiqueta: t('admin.catalogo.menu.eliminar'), peligro: true, onSeleccionar: () => setConfirmacion({ accion: 'eliminar', id }) },
    ];
  }

  /** Sin moneda todavía, el número solo: mejor un instante sin símbolo que con el equivocado. */
  const precio = (valor: number | string) =>
    moneda ? dinero(Number(valor), moneda, idioma) : Number(valor).toFixed(2);

  const columnas: Columna<Item>[] = [
    { id: 'codigo', titulo: t('admin.catalogo.columna.codigo'), ancho: 100, celda: (i) => i.code },
    {
      id: 'nombre',
      titulo: t('admin.catalogo.columna.nombre'),
      // Un botón para quien usa el teclado: la fila entera no recibe el foco.
      celda: (i) => (
        <button type="button" className="tipos__enlace" onClick={() => verFicha(i)}>
          {i.name}
        </button>
      ),
      tarjeta: 'titulo',
    },
    {
      id: 'tipo',
      titulo: t('admin.catalogo.columna.tipo'),
      ancho: 110,
      celda: (i) =>
        i.kind === 'service' ? (
          <Chip tono="marca">{t('admin.catalogo.servicio')}</Chip>
        ) : (
          <Chip>{t('admin.catalogo.producto')}</Chip>
        ),
      // Texto y no etiqueta: en el pie va en gris junto a la categoría.
      tarjeta: {
        lugar: 'pie',
        celda: (i) => (i.kind === 'service' ? t('admin.catalogo.servicio') : t('admin.catalogo.producto')),
      },
    },
    {
      id: 'categoria',
      titulo: t('admin.catalogo.columna.categoria'),
      ancho: 170,
      celda: (i) => i.categoryName ?? '—',
      tarjeta: 'pie',
    },
    {
      id: 'precio',
      titulo: t('admin.catalogo.columna.precio'),
      ancho: 100,
      alinear: 'fin',
      celda: (i) => (i.listPrice === null || i.listPrice === undefined ? '—' : precio(i.listPrice)),
      tarjeta: 'renglon',
    },
    {
      id: 'duracion',
      titulo: t('admin.catalogo.columna.duracion'),
      ancho: 100,
      alinear: 'centro',
      celda: (i) => horas(i.referenceDurationMinutes === null ? null : Number(i.referenceDurationMinutes), idioma),
    },
    {
      id: 'estado',
      titulo: t('admin.catalogo.columna.estado'),
      ancho: 100,
      celda: (i) =>
        i.isActive ? (
          <Chip tono="exito">{t('admin.catalogo.activo')}</Chip>
        ) : (
          <Chip>{t('admin.catalogo.inactivo')}</Chip>
        ),
      tarjeta: 'esquina',
    },
  ];

  const botonNuevo = (
    <Boton icono={<IconoMas />} onClick={() => void navegar(`${RUTA}/nuevo`, { state: { desdeListado: true } })}>
      {t('admin.catalogo.nuevo')}
    </Boton>
  );

  /** Los combos puestos, como se ven en la barra: «Tipo: Servicios», «Categoría: Frenos». */
  function combosPuestos(): string[] {
    const nombreCategoria = categorias.data?.find((c) => String(c.id) === filtros.categoria)?.name;
    return [
      url.tipo !== 'todos' &&
        `${t('admin.catalogo.filtro.tipo')}: ${t(url.tipo === 'service' ? 'admin.catalogo.servicios' : 'admin.catalogo.productos')}`,
      // Sin las categorías cargadas no hay nombre, pero el filtro sigue puesto: se nombra igual.
      filtros.categoria !== 'todas' &&
        (nombreCategoria
          ? `${t('admin.catalogo.filtro.categoria')}: ${nombreCategoria}`
          : t('admin.catalogo.filtro.categoria')),
      url.estado !== 'todos' &&
        `${t('admin.catalogo.filtro.estado')}: ${t(url.estado === 'activos' ? 'admin.catalogo.activo' : 'admin.catalogo.inactivo')}`,
    ].filter((c): c is string => typeof c === 'string');
  }

  /** «Ningún ítem coincide con «turbo» y el filtro Estado: Inactivo.», como en Clientes (#160). */
  function detalleSinResultados(): string {
    const combos = combosPuestos();
    return combos.length === 0
      ? t('admin.catalogo.sinResultados', { texto: url.buscar })
      : t('admin.catalogo.sinResultados.conFiltros', {
          n: combos.length,
          texto: url.buscar,
          filtros: new Intl.ListFormat(idioma, { type: 'conjunction' }).format(combos),
        });
  }

  /**
   * Qué hacer cuando nada coincide, según lo que esté puesto (#169): «Revisa la
   * ortografía» solo si se escribió algo, no cuando lo que vacía la lista es un combo.
   */
  function sugerenciaSinResultados(): string | undefined {
    const hayCombos = combosPuestos().length > 0;
    if (url.buscar && hayCombos) return t('admin.catalogo.sinResultados.buscarYCombos');
    // Solo el buscador: vale la sugerencia de `Listado`, «Revisa la ortografía…».
    if (url.buscar) return undefined;
    if (hayCombos) return t('admin.catalogo.sinResultados.combos');
    // Sin filtros, solo una página que no es la primera: se eliminó su última fila.
    return t('admin.catalogo.sinResultados.pagina');
  }
  const sugerencia = sugerenciaSinResultados();

  // «Más acciones» del Figma: importar abre su ventana; exportar baja el archivo
  // directo, con los filtros puestos. CSV va además de Excel y PDF: son los tres
  // formatos confirmados en #111.
  const masAcciones: ElementoMenu[] = [
    { etiqueta: t('admin.catalogo.menu.importarArchivo'), onSeleccionar: () => setImportando(true) },
    { tipo: 'divisor' },
    { tipo: 'grupo', etiqueta: t('admin.catalogo.exportar.grupo') },
    { etiqueta: t('admin.catalogo.exportar.aExcel'), onSeleccionar: () => void bajar('xlsx') },
    { etiqueta: t('admin.catalogo.exportar.aCsv'), onSeleccionar: () => void bajar('csv') },
    { etiqueta: t('admin.catalogo.exportar.aPdf'), onSeleccionar: () => void bajar('pdf') },
    { tipo: 'nota', texto: t('admin.catalogo.exportar.nota') },
  ];

  return (
    <>
      {problema && <AvisoError aviso={{ titulo: t('admin.catalogo.error.titulo'), texto: problema }} />}
      <ImportarItems abierta={importando} onCerrar={() => setImportando(false)} />
      <ConfirmacionItem pedido={confirmacion} onCerrar={() => setConfirmacion(null)} />

      <Listado
        titulo={t('admin.catalogo.titulo')}
        info={t('admin.catalogo.info')}
        columnas={columnas}
        filas={pagina?.items}
        claveFila={(i) => String(i.id)}
        estado={consulta.isPending ? 'cargando' : consulta.isError ? 'error' : 'listo'}
        onReintentar={() => void consulta.refetch()}
        filtros={
          <>
            <Buscador valor={texto} onCambiar={setTexto} placeholder={t('admin.catalogo.buscar')} />
            <Filtro
              etiqueta={t('admin.catalogo.filtro.tipo')}
              valor={url.tipo}
              onCambiar={(valor) => filtrar({ tipo: valor as FiltrosCatalogo['tipo'] })}
              opciones={[
                { valor: 'todos', etiqueta: t('admin.catalogo.filtro.todos') },
                { valor: 'service', etiqueta: t('admin.catalogo.servicios') },
                { valor: 'product', etiqueta: t('admin.catalogo.productos') },
              ]}
            />
            <Filtro
              etiqueta={t('admin.catalogo.filtro.categoria')}
              valor={filtros.categoria}
              onCambiar={(valor) => filtrar({ categoria: valor })}
              opciones={[
                { valor: 'todas', etiqueta: t('admin.catalogo.filtro.todas') },
                ...(categorias.data ?? []).map((c) => ({ valor: String(c.id), etiqueta: c.name })),
              ]}
            />
            <Filtro
              etiqueta={t('admin.catalogo.filtro.estado')}
              valor={url.estado}
              onCambiar={(valor) => filtrar({ estado: valor as FiltrosCatalogo['estado'] })}
              opciones={[
                { valor: 'todos', etiqueta: t('admin.catalogo.filtro.todos') },
                { valor: 'activos', etiqueta: t('admin.catalogo.activo') },
                { valor: 'inactivos', etiqueta: t('admin.catalogo.inactivo') },
              ]}
            />
          </>
        }
        acciones={
          <>
            <Menu
              ancho={230}
              elementos={masAcciones}
              disparador={({ alternar, atributos }) => (
                <Boton variante="secundario" soloIcono aria-label={t('admin.catalogo.mas')} onClick={alternar} {...atributos}>
                  •••
                </Boton>
              )}
            />
            {botonNuevo}
          </>
        }
        // Una página que no es la primera cuenta como filtro: si queda vacía, el
        // taller no está vacío, y «Aún no hay productos» sería falso.
        hayFiltros={
          url.buscar !== '' ||
          url.tipo !== 'todos' ||
          filtros.categoria !== 'todas' ||
          url.estado !== 'todos' ||
          cursor !== null
        }
        onLimpiarFiltros={() => {
          setTexto('');
          setCursor(null);
          setAnteriores([]);
          setParams({}, { replace: true });
        }}
        // «Sin productos» del Figma: alta e importación a la vista. Lo primero que
        // hace un taller que llega con su catálogo en Excel es importarlo (#169).
        vacio={{
          titulo: t('admin.catalogo.vacio.titulo'),
          texto: t('admin.catalogo.vacio.texto'),
          acciones: (
            <>
              {botonNuevo}
              <Boton variante="secundario" onClick={() => setImportando(true)}>
                {t('admin.catalogo.menu.importarArchivo')}
              </Boton>
            </>
          ),
        }}
        {...(url.buscar && { detalleSinResultados: detalleSinResultados() })}
        {...(sugerencia && { sugerenciaSinResultados: sugerencia })}
        opcionesFila={opciones}
        onFila={verFicha}
        paginacion={{
          hayAnterior: anteriores.length > 0,
          hayMas: pagina?.hasMore ?? false,
          onAnterior: () => {
            setCursor(anteriores.at(-1) ?? null);
            setAnteriores((a) => a.slice(0, -1));
          },
          onSiguiente: () => {
            // Mientras viaja la página siguiente se ve la anterior como relleno, con
            // su cursor viejo: un segundo toque apilaría el mismo dos veces.
            if (!pagina?.nextCursor || consulta.isPlaceholderData) return;
            setAnteriores((a) => [...a, cursor]);
            setCursor(pagina.nextCursor);
          },
        }}
      />
    </>
  );
}
